import { Accordion, Col, Row } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faMedal, faCertificate, faBuilding, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { ATACResultsResponse, ATACRun, getLatestCompletedRun } from 'data-access/atac.api';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const scoreColor = (s: number | null) =>
  s == null ? '#6b7280' : s >= 80 ? '#25b003' : s >= 60 ? '#ffc107' : '#dc3545';

const certStatusColor = (status: string | null | undefined) => {
  if (!status) return '#6b7280';
  if (status === 'Certified') return '#25b003';
  if (status === 'Conditionally Certified') return '#ffc107';
  return '#dc3545';
};

const TrustGauge = ({ score }: { score: number | null }) => {
  const s = score ?? 0;
  const color = scoreColor(score);
  const label = s >= 80 ? 'Certified' : s >= 60 ? 'Conditional' : 'Not Certified';
  return (
    <div className="text-center">
      <div
        style={{
          width: 140,
          height: 140,
          borderRadius: '50%',
          border: `10px solid ${color}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 8px'
        }}
      >
        <span style={{ fontSize: 36, fontWeight: 700, color }}>{score ?? '—'}</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>/ 100</span>
      </div>
      <span className="badge" style={{ backgroundColor: color, fontSize: 12 }}>
        {score != null ? label : 'No score'}
      </span>
    </div>
  );
};

const SummaryCard = ({
  icon,
  label,
  value,
  color
}: {
  icon: IconDefinition;
  label: string;
  value: string | number;
  color: string;
}) => (
  <div className="rounded-3 border p-3 d-flex align-items-center gap-3" style={{ backgroundColor: '#fafafa' }}>
    <div
      className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
      style={{ width: 40, height: 40, backgroundColor: `${color}18` }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
    </div>
    <div>
      <p className="mb-0 text-muted small">{label}</p>
      <p className="mb-0 fw-bold" style={{ fontSize: 20 }}>
        {value}
      </p>
    </div>
  </div>
);

interface ResultsTabProps {
  results: ATACResultsResponse | null;
}

const ResultsTab = ({ results }: ResultsTabProps) => {
  const latestRun: ATACRun | null = getLatestCompletedRun(results?.runs ?? []);

  const summaryCards = latestRun?.summaryCards;
  const trustScore = summaryCards?.trustScore ?? null;
  const radarData = latestRun?.charts?.trustRadar?.dimensions ?? [];
  const evidenceBars = latestRun?.charts?.evidenceBar?.bars ?? [];
  const providerBars = latestRun?.charts?.providerBar?.bars ?? [];
  const modelReg = results?.modelRegistration;

  if (!latestRun) {
    return (
      <div className="text-center text-muted py-5">
        No completed analysis runs yet. Run analysis from the Analysis tab to view results.
      </div>
    );
  }

  return (
    <div>
      <h5 className="fw-semibold mb-4">Trust Assessment Dashboard</h5>

      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={4} xl>
          <SummaryCard
            icon={faShieldHalved}
            label="Trust Score"
            value={summaryCards?.trustScore ?? '—'}
            color={scoreColor(trustScore)}
          />
        </Col>
        <Col xs={12} sm={6} lg={4} xl>
          <SummaryCard
            icon={faMedal}
            label="Trust Grade"
            value={summaryCards?.trustGrade ?? '—'}
            color={scoreColor(trustScore)}
          />
        </Col>
        <Col xs={12} sm={6} lg={4} xl>
          <SummaryCard
            icon={faCertificate}
            label="Certification"
            value={summaryCards?.certificationStatus ?? '—'}
            color={certStatusColor(summaryCards?.certificationStatus)}
          />
        </Col>
        <Col xs={12} sm={6} lg={4} xl>
          <SummaryCard
            icon={faBuilding}
            label="Provider Trust"
            value={summaryCards?.providerTrust ?? '—'}
            color="#305d84"
          />
        </Col>
        <Col xs={12} sm={6} lg={4} xl>
          <SummaryCard
            icon={faFileLines}
            label="Evidence Confidence"
            value={summaryCards?.evidenceConfidence ?? '—'}
            color="#6f42c1"
          />
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xs={12} md={3}>
          <div className="rounded-3 border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-3 text-body">Trust Score Gauge</h6>
            <TrustGauge score={latestRun.charts?.trustGauge?.value ?? trustScore} />
          </div>
        </Col>
        <Col xs={12} md={9}>
          <div className="rounded-3 border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-3 text-body">Trust Principle Radar</h6>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar dataKey="score" stroke="#305d84" fill="#305d84" fillOpacity={0.25} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted small mb-0">No radar chart data available.</p>
            )}
          </div>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xs={12} md={6}>
          <div className="rounded-3 border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-3 text-body">Evidence Coverage</h6>
            {evidenceBars.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={evidenceBars} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: 'transparent' }} />
                  <Bar dataKey="value" name="Score" radius={[4, 4, 0, 0]}>
                    {evidenceBars.map((entry, i) => (
                      <Cell key={i} fill={entry.value >= 80 ? '#25b003' : entry.value >= 60 ? '#ffc107' : '#dc3545'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted small mb-0">No evidence coverage data available.</p>
            )}
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="rounded-3 border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-3 text-body">Provider Trust</h6>
            {providerBars.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={providerBars} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: 'transparent' }} />
                  <Bar dataKey="value" name="Score" fill="#3874ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted small mb-0">No provider trust data available.</p>
            )}
          </div>
        </Col>
      </Row>

      {modelReg && (
        <Accordion defaultActiveKey="">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Model Registration Details</Accordion.Header>
            <Accordion.Body>
              <Row className="g-2 small">
                {modelReg.provider && (
                  <Col md={6}>
                    <strong>Provider:</strong> {modelReg.provider}
                  </Col>
                )}
                {modelReg.modelName && (
                  <Col md={6}>
                    <strong>Model Name:</strong> {modelReg.modelName}
                  </Col>
                )}
                {modelReg.modelVersion && (
                  <Col md={6}>
                    <strong>Model Version:</strong> {modelReg.modelVersion}
                  </Col>
                )}
                {modelReg.deploymentType && (
                  <Col md={6}>
                    <strong>Deployment Type:</strong> {modelReg.deploymentType}
                  </Col>
                )}
                {modelReg.intendedUse && (
                  <Col xs={12}>
                    <strong>Intended Use:</strong> {modelReg.intendedUse}
                  </Col>
                )}
                {modelReg.riskClassification && (
                  <Col md={6}>
                    <strong>Risk Classification:</strong> {modelReg.riskClassification}
                  </Col>
                )}
                {modelReg.assessmentOwner && (
                  <Col md={6}>
                    <strong>Assessment Owner:</strong> {modelReg.assessmentOwner}
                  </Col>
                )}
                {modelReg.assessmentDate && (
                  <Col md={6}>
                    <strong>Assessment Date:</strong> {modelReg.assessmentDate}
                  </Col>
                )}
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
};

export default ResultsTab;
